import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { App } from 'antd';

import { getHome, pinTargetOf, setChartPinSize, setFavoriteOrder, setPin, withPinned } from '../services/favorites';
import { useOptionalFavorites } from '../contexts/FavoritesContext';
import { normalizeId } from '../utils/ids';
import { orderByFavorites } from '../utils/collections';
import { isTokenValid } from '../utils';
import { getSessionUserId, onSessionChange, useOnSessionChange } from '../utils/session';

const SECTION_KEYS = ['pinned', 'charts', 'collections'];

// Which Home section a pin of this kind lives in (keepr-api favoriteUtils
// SECTIONS): a chart its own; every other pin — launchers of every kind,
// records, tags, combined lists — the ONE Pinned strip (KPR-1123).
const sectionOfPin = pin => (pin?.targetType === 'chartHome' ? 'charts' : 'pinned');

// A Home read with one section replaced. Pinned goes through withPinned, so
// the per-kind arrays derived from it (the rail's pinned tags, the palette)
// move with it.
const withSection = (home, section, list) => (section === 'pinned' ? withPinned(home, list) : { ...home, [section]: list });

// ---------------------------------------------------------------- the shared read
//
// One Home read per session, shared by every mounted instance (the rail, the
// page, the palette, the tags window), the way hooks/useMe shares its answer:
// a module store keyed on the account and FavoritesContext.pinsVersion. A pin
// change anywhere bumps the version, which is the one re-read; an instance
// that is `enabled: false` subscribes without asking. The store is cleared on
// a session change, and when the last instance unmounts (so a test, or a
// return to a signed-in tab with nothing mounted, starts from a fresh read).

const freshStore = () => ({ key: null, user: null, promise: null, value: null, error: null, busy: false });
let store = freshStore();
const subscribers = new Set();
const notify = () => [...subscribers].forEach(check => check());

const NOTHING = Object.freeze({ home: null, error: null, busy: false });

const keyOf = (user, pinsVersion) => `${user || ''}|${pinsVersion}`;

// What an instance sees for its key. A re-read after a pin keeps showing the
// last answer for the same account (stale-while-revalidate); another
// account's is never shown.
function readView(key, user) {
	if (store.user !== user) return NOTHING;
	return { home: store.value, error: store.error, busy: store.key === key && store.busy };
}

function ensureRead(key, { force = false } = {}) {
	const user = getSessionUserId();
	if (!force && store.key === key && store.promise && (store.busy || !store.error)) return;
	if (store.user !== user) {
		store.value = null;
		store.error = null;
	}
	store.key = key;
	store.user = user;
	store.busy = true;
	const promise = getHome()
		.then(data => {
			if (store.promise !== promise || getSessionUserId() !== user) return;
			store.value = data === null ? false : data;
			store.error = null;
			store.busy = false;
			notify();
		})
		.catch(err => {
			if (store.promise !== promise || getSessionUserId() !== user) return;
			store.error = err;
			store.busy = false;
			store.value = store.value || false;
			notify();
		});
	store.promise = promise;
	notify();
}

// An optimistic write: the shared value, seen by every instance.
function writeShared(next) {
	store.value = typeof next === 'function' ? next(store.value) : next;
	notify();
}

onSessionChange(() => {
	store = freshStore(); // whatever is in flight answers for the last account
	notify();
});

/** Test seam: forget the shared read. */
export function _resetHomeStore() {
	store = freshStore();
}

const sameList = (a, b) => a.length === b.length && a.every((entry, i) => Object.is(entry, b[i]));

// Re-render this instance only when what it selects from the store changed.
function useStoreSelection(select, key, user) {
	const [, tick] = useReducer(n => n + 1, 0);
	const selected = select(readView(key, user));
	const selectedRef = useRef(selected);
	selectedRef.current = selected;
	const watchRef = useRef({ select, key, user });
	watchRef.current = { select, key, user };
	useEffect(() => {
		const check = () => {
			const watch = watchRef.current;
			if (!sameList(watch.select(readView(watch.key, watch.user)), selectedRef.current)) tick();
		};
		subscribers.add(check);
		return () => {
			subscribers.delete(check);
			if (!subscribers.size) store = freshStore();
		};
	}, []);
	return readView(key, user);
}

const selectAll = view => [view.home, view.error, view.busy];
const selectTags = view => (view.home && view.home.tags) || [];

/**
 * The signed-in Home page's data: GET /api/home — the caller's pins resolved
 * and access-filtered — plus the two things the page does to it.
 *
 *   busy      a request is in flight (loading is only "nothing has answered")
 *   home      { pinned, launchers, records, tags, combinedLists, charts,
 *               collections, collectionGroups, favoriteCards, personal,
 *               dropped } | null while loading |
 *               false on an API that has no Home yet (the page then shows
 *               Recent alone). `tags` are pinned tag pages and
 *               `collectionGroups` the person's private tags on collections
 *               (tags T5).
 *               `pinned` is the ONE Pinned strip (KPR-1123); the four
 *               per-kind arrays are derived from it (services/favorites
 *               withPinned) and change only with it.
 *   reorder(section, ids)   the section's new order ('pinned' | 'charts' |
 *                           'collections'), by pin ROW id; optimistic,
 *                           rolled back with a message on refusal
 *   unpin(pin)              optimistic, the same
 *   resize(pin, size)       a chart pin small or wide (charts A7, ruled
 *                           d18); optimistic, the same
 *   collections             the favorite collections, in the ONE
 *                           favorites order (FavoritesContext) — the rail's
 *                           and the collections page's, so the three never
 *                           disagree. DERIVED from the favorites once they
 *                           have loaded: an unstar anywhere leaves the strip
 *                           at once and a star joins it (drawn from the record
 *                           it was starred from), with no second Home read. A
 *                           favorite the Home read left out (no longer
 *                           readable) stays out; until the favorites answer,
 *                           it is home.collections in that order.
 *   reorderCollections(collectionIds)
 *                           saves that order through FavoritesContext, so
 *                           a reorder here moves the rail and the
 *                           collections page too (without the provider — a
 *                           test host — the section's own rows by row id)
 *
 * It re-reads whenever a pin changes anywhere (FavoritesContext bumps
 * `pinsVersion` — the item ⋯ menu, a quick add's menu, a launch row's pin),
 * so coming back to Home after pinning shows the pin. `enabled: false`
 * defers the fetch (the command palette reads Home only once it is open).
 *
 * Per account: a sign-out or another account signing in clears it at once
 * and drops any answer still in flight. The shell's palette holds this hook
 * through the /logout hop, so without that the next person at the keyboard
 * was offered the last one's pinned launchers by name.
 */
export default function useHome({ enabled = true } = {}) {
	const { message } = App.useApp();
	const favorites = useOptionalFavorites();
	const pinsVersion = favorites?.pinsVersion || 0;
	const session = useOnSessionChange(() => {});
	const key = keyOf(session, pinsVersion);
	const { home, error, busy } = useStoreSelection(selectAll, key, session);

	const load = useCallback(() => {
		if (!enabled || !isTokenValid()) return;
		ensureRead(key, { force: true });
	}, [enabled, key]);

	// The first instance to ask for a key reads; the rest share its answer.
	useEffect(() => {
		if (!enabled || !isTokenValid()) return;
		ensureRead(key);
	}, [enabled, key]);

	const reorder = useCallback(async (section, orderedIds) => {
		if (!SECTION_KEYS.includes(section)) return;
		const before = store.value;
		if (!before) return;
		const byId = new Map((before[section] || []).map(pin => [normalizeId(pin._id), pin]));
		const next = orderedIds.map(id => byId.get(normalizeId(id))).filter(Boolean);
		writeShared(withSection(before, section, next));
		try {
			await setFavoriteOrder(section, next.map(pin => normalizeId(pin._id)));
			// The rail draws the same collection order from the favorites cache.
			if (section === 'collections') favorites?.ensureLoaded?.({ force: true });
		} catch (err) {
			writeShared(store.value === null ? before : withSection(store.value, section, before[section]));
			message.error('Could not save that order. Please try again.');
		}
	}, [favorites, message]);

	const unpin = useCallback(async (pin) => {
		const before = store.value;
		if (!before || !pin) return;
		const section = sectionOfPin(pin);
		const rowId = normalizeId(pin._id);
		writeShared(withSection(before, section, (before[section] || []).filter(entry => normalizeId(entry._id) !== rowId)));
		try {
			await setPin(pin.targetType, pinTargetOf(pin), false);
			// Every other surface's "Pinned" state follows (and this hook
			// re-reads, which changes nothing it has not already shown).
			favorites?.notePinsChanged?.();
		} catch (err) {
			writeShared(current => (current ? withSection(current, section, before[section]) : before));
			message.error('Could not unpin that. Please try again.');
		}
	}, [favorites, message]);

	const resize = useCallback(async (pin, size) => {
		const before = store.value;
		if (!before || !pin) return;
		const rowId = normalizeId(pin._id);
		const sized = list => (list || []).map(entry => (normalizeId(entry._id) === rowId ? { ...entry, size } : entry));
		writeShared({ ...before, charts: sized(before.charts) });
		try {
			await setChartPinSize({ targetId: normalizeId(pin.target_id), collectionId: normalizeId(pin.collection_id) }, size);
		} catch (err) {
			writeShared(current => (current ? { ...current, charts: before.charts } : before));
			message.error('Could not change that chart\'s size. Please try again.');
		}
	}, [message]);

	const favoriteOrder = favorites?.favoriteCollectionOrder;
	const favoritesLoaded = Boolean(favorites?.favoriteCollectionsLoaded);
	const favoriteRecords = favorites?.favoriteCollectionRecords;
	const collections = useMemo(() => {
		const rows = (home && home.collections) || [];
		if (!home || !favoritesLoaded || !Array.isArray(favoriteOrder)) {
			return orderByFavorites(rows, favoriteOrder, pin => pin?.target_id);
		}
		// The favorites are the list; the Home read is where a row's identity
		// (and its readability) comes from. A star made on this tab since the
		// read has no row there — its record stands in, shaped as a pin.
		const personalId = normalizeId(home.personal?._id);
		const byTarget = new Map(rows.map(pin => [normalizeId(pin?.target_id), pin]));
		return favoriteOrder
			.filter(id => id && id !== personalId)
			.map(id => {
				if (byTarget.has(id)) return byTarget.get(id);
				const record = favoriteRecords?.get?.(id);
				return record ? { _id: null, targetType: 'collection', target_id: id, collection_id: null, collection: record } : null;
			})
			.filter(Boolean);
	}, [home, favoritesLoaded, favoriteOrder, favoriteRecords]);

	const reorderCollections = useCallback((collectionIds) => {
		if (typeof favorites?.reorderCollections === 'function') return favorites.reorderCollections(collectionIds);
		const rowOf = new Map((store.value?.collections || []).map(pin => [normalizeId(pin.target_id), normalizeId(pin._id)]));
		return reorder('collections', (collectionIds || []).map(id => rowOf.get(normalizeId(id))).filter(Boolean));
	}, [favorites, reorder]);

	return { home, loading: home === null, busy, error, reload: load, reorder, unpin, resize, collections, reorderCollections };
}

/**
 * Only Home's pinned tags (the rail's), from the same shared read. It
 * re-renders when the tags change and not for a launcher or chart reorder.
 * `enabled: false` subscribes without asking.
 */
export function useHomeTags({ enabled = true } = {}) {
	const favorites = useOptionalFavorites();
	const session = useOnSessionChange(() => {});
	const key = keyOf(session, favorites?.pinsVersion || 0);
	const view = useStoreSelection(selectTags, key, session);
	useEffect(() => {
		if (!enabled || !isTokenValid()) return;
		ensureRead(key);
	}, [enabled, key]);
	return view.home ? view.home.tags || [] : null;
}
